import React, { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import Header from './Header';
import { Missionary } from '../supabase/types';
import { insertSubscriber } from '../supabase/supabase';
import { Button, Modal, Form } from 'react-bootstrap';
import LoadingLogo from './LoadingLogo';
import { getMissionaryByUniqueId } from '../api';

const Subscribe = () => {
    const [missionary, setMissionary] = useState<Missionary | null>();
    const [email, setEmail] = useState<string>('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const openConfirmation = () => setShowConfirmation(true);
    const closeConfirmation = () => setShowConfirmation(false);

    const { id } = useParams();

    useEffect(() => {
        const fetchMissionary = async () => {
            try {
                if (id) {
                    const response = await getMissionaryByUniqueId(id);
                    setMissionary(response);
                }
            } catch (error) {
                console.error('Error fetching missionary:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMissionary();
    }, [id]);

    // Hide success message after 60 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSuccessMessage(false);
        }, 2500);
        return () => clearTimeout(timer);
    }, [showSuccessMessage]);

    const handleFinalSubmit = async () => {
        try {
            await insertSubscriber({ email: email, verified: true, active: true, missionary: id! });
            setEmail(''); // Clear email field after successful submission
            setShowSuccessMessage(true);
            console.log('Subscription successful');
        } catch (error) {
            console.error('Error creating subscriber:', error);
            // Handle subscription error (e.g., display error message)
        } finally {
            closeConfirmation();
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        openConfirmation();
    };

    return (
        <div className="subscribe-page">
            <Header />

            <div className="subscribe-container">
                {isLoading ? (
                    <LoadingLogo />
                ) : missionary && !showSuccessMessage ? (
                    <div className="subscribe-card">
                        <div className="missionary-info">
                            <h2 className="missionary-name">{missionary.name}</h2>
                            <p className="mission-details">
                                Will be serving in <strong>{missionary.assigned_mission}</strong>
                            </p>
                            <p className="start-date">
                                Starting{' '}
                                <strong>
                                    {new Date(missionary.start_date).toLocaleDateString()}
                                </strong>
                            </p>
                        </div>

                        <Form onSubmit={handleSubmit} className="subscribe-form">
                            <div className="form-header">
                                <h3>Stay Connected</h3>
                                <p>
                                    Get email updates from <strong>{missionary.name}</strong>
                                </p>
                            </div>

                            <div className="form-group">
                                <Form.Control
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="modern-input"
                                    placeholder="Enter your email address"
                                />
                            </div>

                            <Button type="submit" className="subscribe-btn" size="lg">
                                Subscribe for Updates
                            </Button>
                        </Form>
                    </div>
                ) : showSuccessMessage && missionary ? (
                    <div className="success-card">
                        <div className="success-icon">✓</div>
                        <h3>You're subscribed!</h3>
                        <p>
                            Thank you for subscribing! You'll receive updates about{' '}
                            {missionary.name}'s mission.
                        </p>
                    </div>
                ) : !missionary ? (
                    <div className="error-card">
                        <h3>Missionary Not Found</h3>
                        <p>
                            We couldn't find the missionary you're looking for. Please check the
                            link and try again.
                        </p>
                    </div>
                ) : null}
            </div>

            <Modal show={showConfirmation} onHide={closeConfirmation} className="modern-modal">
                <Modal.Header closeButton className="modal-header">
                    <Modal.Title className="modal-title">Confirm Subscription</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modal-body">
                    <p>Subscribe with this email address?</p>
                    <div className="email-confirm">{email}</div>
                </Modal.Body>
                <Modal.Footer className="modal-footer">
                    <Button
                        variant="outline-secondary"
                        onClick={closeConfirmation}
                        className="cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleFinalSubmit} className="save-btn">
                        Subscribe
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Subscribe;
